import {
  ClickAwayListener,
  // eslint-disable-next-line no-restricted-imports
  Tooltip as MUITooltip,
  TooltipProps as MUITooltipProps,
  useMediaQuery,
} from '@mui/material';
import React, { useState } from 'react';

export type TooltipProps = MUITooltipProps & {
  nonInteractable?: boolean;
  hideWhenRefIsHidden?: boolean;
  tapToOpenOnMobile?: boolean;
};

// ref can be any type
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Tooltip = React.forwardRef<any, TooltipProps>((props, ref) => {
  const {
    nonInteractable = true,
    hideWhenRefIsHidden = false,
    tapToOpenOnMobile = false,
    ...restProps
  } = props;

  const isTouchDevice = useMediaQuery('(pointer: coarse)');
  const useTapMode = tapToOpenOnMobile && isTouchDevice;

  const [tapOpen, setTapOpen] = useState(false);

  const enhancePopperSlot = (
    slot: NonNullable<MUITooltipProps['slotProps']>['popper'] | undefined,
  ): NonNullable<MUITooltipProps['slotProps']>['popper'] => {
    const mergeWithStyles = (resolved?: Record<string, unknown>) => {
      const base = { ...(resolved ?? {}) } as Record<string, unknown> & {
        sx?: object;
      };

      return {
        ...base,
        sx: {
          ...(base.sx ?? {}),
          ...(nonInteractable && { pointerEvents: 'none' }),
          /* Hide the popper when the reference is hidden
             https://popper.js.org/docs/v2/modifiers/hide/
           */
          ...(hideWhenRefIsHidden && {
            '&[data-popper-reference-hidden]': {
              visibility: 'hidden',
              pointerEvents: 'none',
            },
          }),
        },
      } as Record<string, unknown>;
    };

    if (typeof slot === 'function') {
      return ((ownerState) =>
        mergeWithStyles(slot(ownerState) as Record<string, unknown>)) as NonNullable<
        MUITooltipProps['slotProps']
      >['popper'];
    }

    return mergeWithStyles(slot as Record<string, unknown>) as unknown as NonNullable<
      MUITooltipProps['slotProps']
    >['popper'];
  };

  const popperSlotProps = enhancePopperSlot(restProps.slotProps?.popper);

  const tooltipElement = (
    <MUITooltip
      enterDelay={1000}
      {...restProps}
      ref={ref}
      disableInteractive={nonInteractable}
      slotProps={{
        ...restProps.slotProps,
        popper: popperSlotProps,
      }}
      {...(useTapMode && {
        open: tapOpen,
        disableHoverListener: true,
        disableFocusListener: true,
        disableTouchListener: true,
        children: (() => {
          const child = restProps.children as React.ReactElement<{
            onClick?: (e: React.MouseEvent) => void;
          }>;
          return React.cloneElement(child, {
            onClick: (e: React.MouseEvent) => {
              child.props.onClick?.(e);
              setTapOpen((prev) => !prev);
            },
          });
        })(),
      })}
    />
  );

  if (useTapMode) {
    return (
      <ClickAwayListener onClickAway={() => setTapOpen(false)}>
        {tooltipElement}
      </ClickAwayListener>
    );
  }

  return tooltipElement;
});

Tooltip.displayName = 'Tooltip';
